export type ToolCategory = "Scoping" | "Ideation" | "Validation" | "Business Model" | "Pitching";

export interface Tool {
  id: string;
  title: string;
  description: string;
  category: ToolCategory;
  modules: number[];
  printSize: string; // e.g. "A1", "A2", "A3", "A4"
  downloadUrl: string | null;
}

export const TOOLS: Tool[] = [
  // ── Scoping ─────────────────────────────────────────────────────────────
  {
    id: "scoping-canvas",
    title: "Scoping Canvas",
    description:
      "Define the challenge space before fieldwork begins. Eight sections cover the HMW framing, why this challenge matters strategically, customer segment, current situation, underlying assumptions, open questions, goal/success definition, and related initiatives. Use it in Module 1 to align the full team on the problem before any research begins.",
    category: "Scoping",
    modules: [1],
    printSize: "A3",
    downloadUrl: "https://drive.google.com/file/d/1ehmWQHz_c86i2QVgoMFDz5wkXXW0N9LA/view",
  },
  {
    id: "customer-journey-map",
    title: "Customer Journey Map",
    description:
      "Visualise the end-to-end experience of your customer segment across four layers: Phases, Actions, Feelings (drawn as an emotion curve), and Channels. Spot the pain points and problem zones that deserve the most attention — the output feeds directly into problem scoring.",
    category: "Scoping",
    modules: [1],
    printSize: "A1",
    downloadUrl: "https://drive.google.com/file/d/15aUVbUMAs7PrmF7UQoAS3BavSe4fu7Qh/view",
  },
  {
    id: "problem-sizing",
    title: "Problem Sizing",
    description:
      "A quick guesstimate of the annual value of a user problem — no spreadsheets needed. Multiply user segment size × frequency × severity per occurrence to get an annual problem size, then factor in PESTEL boosters and setbacks to stress-test how the opportunity will grow or shrink over time.",
    category: "Scoping",
    modules: [1, 3],
    printSize: "A3",
    downloadUrl: "https://drive.google.com/file/d/1KHBxbAlXy7gqzptPLMJWUk3R2CN3WPkN/view",
  },

  {
    id: "problem-validation-questions",
    title: "Problem Validation Questions",
    description:
      "A structured question bank for empathy interviews. Covers four phases of the research process — finding the right people, interview preparation, conducting the session, and analysis. Use it to build your interview guide in Module 1 and to design validation experiments in Module 2.",
    category: "Scoping",
    modules: [1],
    printSize: "A4",
    downloadUrl: "https://drive.google.com/file/d/1wCtF6xuq-8c8SEMpiWDXp_HLN2CTu0u3/view",
  },

  // ── Ideation ─────────────────────────────────────────────────────────────
  {
    id: "hmw-statement-builder",
    title: "HMW Statement Builder",
    description:
      "Turn research insights into ideation springboards. Build a How Might We statement from three components — Action, Subject, and desired Outcome — then apply 8 reframing lenses (highlight the good, remove the bad, flip it, question an assumption, add adjectives, leverage unexpected resources, use analogy, change the status quo) to generate multiple HMW variations before choosing your top one.",
    category: "Ideation",
    modules: [2],
    printSize: "A4",
    downloadUrl: "https://drive.google.com/file/d/1fQWyB21IUhJq4w8RApg2ulAJ3FQPZbSd/view",
  },
  {
    id: "concept-card",
    title: "Concept Card",
    description:
      "Develop a post-it idea into a full concept. Capture the customer, problem, solution (written and sketched), first revenue model ideas, and plot the concept on a radical/not-radical axis. A completed card should be self-explanatory to anyone without you in the room — anonymous by design so ideas are judged on merit.",
    category: "Ideation",
    modules: [2, 3],
    printSize: "A3",
    downloadUrl: "https://drive.google.com/file/d/1ch67Axt0wc-FwjAnaXxU3Ddm2IoL_HX_/view",
  },
  {
    id: "storyboard",
    title: "Storyboard",
    description:
      "An 8-frame grid for visualising the user journey with your concept. Draw and caption how your customer encounters, uses, and benefits from your solution scene by scene. Reveals gaps and assumptions that are invisible in a concept card — use it alongside the AI challenger prompt to surface weak spots before assumption mapping.",
    category: "Ideation",
    modules: [2],
    printSize: "A4",
    downloadUrl: "https://drive.google.com/file/d/1JB5GEA17-VXYT1Ckn_QmU_Vzr4HI3taF/view",
  },

  // ── Validation ───────────────────────────────────────────────────────────
  {
    id: "assumption-mapper",
    title: "Assumption Mapper",
    description:
      "A 2×2 matrix plotting your concept's assumptions by importance (concept-breaking vs. survivable if wrong) and testability (quick vs. resource-intensive). The top-left quadrant — important and easy to test — is where you start. Ensures the team focuses validation effort on what matters most rather than testing everything at once.",
    category: "Validation",
    modules: [2, 3],
    printSize: "A1",
    downloadUrl: "https://drive.google.com/file/d/1um4EYy6CJeEaXxTzB9e8VIfwjktIcn-m/view",
  },
  {
    id: "experiment-picker",
    title: "Experiment Picker",
    description:
      "A visual decision-tree flowchart covering 24 experiment types across 5 categories: exploratory (interviews, generative sessions), conceptual (wireframes, app mockups, comprehension tests), behavioral (landing pages, A/B testing, ad campaigns), market (crowdfunding, pre-sales, letters of intent), and functional (concierge, Wizard of Oz, process simulation). Guides teams to the right experiment for their stage.",
    category: "Validation",
    modules: [2, 3],
    printSize: "A1",
    downloadUrl: "https://drive.google.com/file/d/18-49lXntyXAJ1aEdhr3xqb6xR48ADFUd/view",
  },
  {
    id: "experiment-card",
    title: "Experiment Card",
    description:
      "Design a single lean experiment. Define the type of experiment, what needs to be tested and how, the target audience, success criteria, and a practical task timeline. Use one card per assumption — don't prototype everything when you can test the critical unknowns first with minimal cost and time.",
    category: "Validation",
    modules: [2, 3],
    printSize: "A4",
    downloadUrl: "https://drive.google.com/file/d/1CFOwi7rBIM2OB8UOoPlk2qha26xDLLA_/view",
  },
  {
    id: "validation-guide",
    title: "Validation Guide",
    description:
      "A detailed reference guide to all 24 experiment types in the Experiment Picker. Describes each method — when to use it, how to run it, what good results look like, and common pitfalls. Keep it on hand when designing experiments and deciding what success criteria to set on your Experiment Cards.",
    category: "Validation",
    modules: [2, 3],
    printSize: "A4",
    downloadUrl: "https://drive.google.com/file/d/1xr6hLtk_0296lIkqqsMUvtY7tKNoQ4mY/view",
  },

  // ── Business Model ────────────────────────────────────────────────────────
  {
    id: "business-model-kit",
    title: "Business Model Kit",
    description:
      "A physical kit for building a visual prototype of your business model. Coloured cards are the actors, grey cards are what gets exchanged between them, and arrows show the direction each thing flows. Lay it out on the table to make every value exchange explicit, then photograph the result and upload it to Miro. Used in Module 3 to build, challenge and iterate the model behind your concept.",
    category: "Business Model",
    modules: [3],
    printSize: "Kit",
    downloadUrl: null,
  },
  {
    id: "business-model-canvas",
    title: "Business Model Canvas",
    description:
      "The classic Osterwalder 9-block canvas — Key Partners, Key Activities, Key Resources, Value Propositions, Customer Relationships, Channels, Customer Segments, Cost Structure, and Revenue Streams — with facilitation prompts for each block. Use it in Module 3 to stress-test the full business logic behind your validated concept.",
    category: "Business Model",
    modules: [3],
    printSize: "A2",
    downloadUrl: "https://drive.google.com/file/d/1RO4krO8eI5YV2hM3M3-zZN-99uDdQb_H/view",
  },
  {
    id: "vision-card",
    title: "Vision Card",
    description:
      "Clarify and communicate the vision behind your solution in 9 blocks: who it's for, the problem they have, what you offer, first revenue model ideas, why it will work, why it might fail, top 4 features, next steps, and a Value Proposition Statement. Use it to align on your solution with sponsors and stakeholders.",
    category: "Business Model",
    modules: [3],
    printSize: "A3",
    downloadUrl: "https://drive.google.com/file/d/15iFnY2vvjQYjw7Og6Yb5FsOag9qgo_Ll/view",
  },
  {
    id: "ballpark-figures",
    title: "Ballpark Figures",
    description:
      "A back-of-envelope revenue model. Multiply buyer or user × frequency × addressable market potential × price to arrive at a revenue potential figure. Forces agreement on a handful of key assumptions rather than a detailed financial model. Update the numbers after each validation round as assumptions are confirmed or disproved.",
    category: "Business Model",
    modules: [3],
    printSize: "A3",
    downloadUrl: "https://drive.google.com/file/d/1xOK_DW8n5hmNRAeTlELIFBkXGhtP4g_x/view",
  },

  // ── Pitching ─────────────────────────────────────────────────────────────
  {
    id: "pitching-canvas",
    title: "Pitching Canvas",
    description:
      "Structure your pitch storyline before building the deck. Six sections mirror a complete pitch arc: Problem to solve (human-centred), Solution/offering, Value of solution (with ballpark figures), Credibility (validated assumptions and experiment results), Call to action/Next steps, and Q&A preparation. Build and test the story here first — the deck comes after.",
    category: "Pitching",
    modules: [3, 4],
    printSize: "A1",
    downloadUrl: "https://drive.google.com/file/d/17h7FQcWTyc8p2QS87mi7ycBO2uNfNCXK/view",
  },
  {
    id: "pitch-evaluation-sheet",
    title: "Pitch Evaluation Sheet",
    description:
      "A structured scoring tool for teams and jury members. Evaluates a pitch across 5 dimensions — problem clarity, solution quality, business model value, credibility/evidence, and next steps — with 3–4 specific questions per dimension rated on a Big No → Big Yes scale. Use it in dry-run sessions and on pitch day to give and receive structured feedback.",
    category: "Pitching",
    modules: [3, 4],
    printSize: "A4",
    downloadUrl: "https://drive.google.com/file/d/1IYxa5zXsC1Eonq6dH1R57yoXgNzAO5rj/view",
  },
  {
    id: "pitching-tips",
    title: "Pitching Tips: Verbal & Visual Communication",
    description:
      "A quick-reference guide for delivery and slide design. Verbal: repeat important points, use silence, engage with short questions, use metaphors, manage nerves and posture, and use Q&A time honestly. Visual: give slides room to breathe, one key message per slide, consistent visual identity, clear hierarchy, and legible fonts — while avoiding clutter, complex graphs, clashing colours, and irrelevant content.",
    category: "Pitching",
    modules: [3, 4],
    printSize: "A4",
    downloadUrl: "https://drive.google.com/file/d/1ADx5MgGo46-aP8PlSLETGeqGC564prbA/view",
  },
];
